import React, {createContext, useContext, useState, useEffect, ReactNode} from 'react';
import {ServiceContainer} from '../../shared/di/ServiceContainer';
import {User} from '../../domain/auth/entities/User';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  needs2FA: boolean;
  login: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({children}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const authService = ServiceContainer.getInstance().getAuthService();

  const checkAuth = async () => {
    try {
      // Agregar timeout de 5 segundos
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Auth check timeout')), 5000)
      );
      
      const currentUser = await Promise.race([
        authService.getCurrentUser(),
        timeoutPromise
      ]) as User | null;
      
      setUser(currentUser);
    } catch (error) {
      console.error('[AuthContext] Error checking auth:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const loggedInUser = await authService.login({email, password});
      setUser(loggedInUser);
      return loggedInUser;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      // No forzar recarga de página, dejar que React Navigation maneje la navegación
    } catch (error) {
      console.error('[AuthContext] Error during logout:', error);
      // Even if logout fails, clear user
      setUser(null);
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    isAuthenticated: user !== null && !!user.token && !user.temp_token,
    needs2FA: user !== null && !!user.temp_token,
    login,
    logout,
    refreshAuth: checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};
